import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { fadeIn, hoverEffect } from "../Other Components/animations";
import useNarrowMode from "../../hooks/useNarrowModeChecker";
import Button from "../Other Components/button";
import InputField from "../Other Components/inputField";
import "./itemCard.css";

export default function ItemCard({
  ItemID,
  selectedItemIds,
  setIDSelected,
  className = "item",
  propertiesToShow = [
    {
      Value: "/",
      columnWidth: 1,
      Scrollable: false,
    },
  ],
  bBtnVisible = true,
  bBtnSectionVisible = true,
  btnSectionWidth = 1,
  btnText,
  onClick,
  onSecondAction,
  bBodyClickable = false,
  bSelectable = false,
  onSelected,
  bCountable = false,
  bEditEnabled = true,
  Amounts,
  onChangeAmount,
  bDisabled,
}) {
  const [propertiesLocal, setPropertiesLocal] = useState(propertiesToShow);
  const isNarrow = useNarrowMode();
  const [bodyClickLocal, setBodyClickLocal] = useState(bBodyClickable);
  const [btnVisibleLoc, setBtnVisibleLoc] = useState(bBtnVisible);
  const [btnSectionVisibleLoc, setBtnSectionVisibleLoc] =
    useState(bBtnSectionVisible);

  useEffect(() => {
    setBodyClickLocal(isNarrow || bBodyClickable);
    setBtnVisibleLoc(!isNarrow && bBtnVisible);
    setBtnSectionVisibleLoc(!isNarrow && bBtnSectionVisible);
  }, [isNarrow]);

  useEffect(() => {
    setPropertiesLocal(propertiesToShow);
  }, [propertiesToShow]);

  const createColumns = () => {
    return propertiesLocal.map((item, index) => {
      var finalItem = { ...item };

      let ColumnLabel = finalItem.Value;

      if (finalItem?.Label) ColumnLabel = finalItem.Label;

      if (typeof item.customViewFunction === "function") {
        finalItem.Value = item.customViewFunction(item.Value);
      }
      console.log("ItemCard: finalItem", finalItem, "ColumnLabel", ColumnLabel);
      return (
        <div
          key={index}
          className={
            className +
            " card-column" +
            (finalItem.Editable && finalItem.Countable ? " Countable" : "")
          }
          style={{
            ...(finalItem.Scrollable && {
              overflowX: "auto",
              overflowY: "hidden",
            }),
          }}
        >
          {finalItem.Color ? (
            <div
              className="item-card-color-circle"
              style={{ backgroundColor: finalItem.Color }}
            ></div>
          ) : null}
          {ColumnLabel !== null && ColumnLabel !== undefined
            ? ColumnLabel
            : "/"}
        </div>
      );
    });
  };

  const onBodyClick = () => {
    if (!bodyClickLocal) return;
    if (bSelectable) {
      const newSelectedState =
        selectedItemIds !== undefined && !selectedItemIds.includes(ItemID);
      onSelectedLocal(newSelectedState, ItemID);
    } else if (onClick) onEditBtnClick();
  };

  const onEditBtnClick = () => {
    if (bEditEnabled) if (onClick) onClick();
  };

  const onSelectedLocal = (newSelectedState, ItemID) => {
    setIDSelected(ItemID, newSelectedState);
    if (onSelected !== undefined && typeof onSelected === "function")
      onSelected(newSelectedState, ItemID);
  };

  const holdDelay = 600;
  let holdTimer = null;
  let heldDownActivated = false;

  const handleContextMenu = (e) => {
    if (onSecondAction) {
      e.preventDefault();
      localSecondAction(ItemID);
    }
  };

  const startHoldTimer = () => {
    if (onSecondAction) {
      holdTimer = setTimeout(() => {
        if (!heldDownActivated) {
          heldDownActivated = true;
          localSecondAction(ItemID);
          holdTimer = null;
        }
      }, holdDelay);
    }
  };

  const cancelHoldTimer = () => {
    if (holdTimer) {
      clearTimeout(holdTimer);
      holdTimer = null;
    }
  };

  const localSecondAction = (ItemID) => {
    if (navigator.vibrate) {
      navigator.vibrate(1);
    }
    onSecondAction(ItemID);
    heldDownActivated = false;
    clearTimeout(holdTimer);
  };

  const calculateGridColumns = () => {
    const contentColumns = propertiesLocal
      .map((item) => `${item.columnWidth}fr`)
      .join(" ");

    if (!btnSectionVisibleLoc) {
      return `${contentColumns} 0fr`;
    }

    const totalContentFr = propertiesLocal.reduce(
      (sum, item) => sum + item.columnWidth,
      0
    );

    const minButtonFr = totalContentFr * 0.15;
    const actualButtonFr = Math.max(btnSectionWidth, minButtonFr);

    return `${contentColumns} ${actualButtonFr}fr`;
  };

  const gridColumns = calculateGridColumns();

  const itemCardClassName =
    className +
    +" " +
    ItemID +
    " Card" +
    (selectedItemIds !== undefined && selectedItemIds.includes(ItemID)
      ? " selected"
      : "") +
    (bDisabled ? " disabled" : "") +
    (bodyClickLocal ? " clickable" : "") +
    (isNarrow ? " narrowCard" : "") +
    (bCountable ? " countable" : "");

  const changeAmount = (amount) => {
    if (amount < 0) amount = 0;
    if (containsProperty("Price"))
      onChangeAmount(
        ItemID,
        amount,
        getPropertyByName("Price").Value,
        getPropertyByName("Amount").Value
      );
    else onChangeAmount(ItemID, amount);
  };

  const containsProperty = (prop) => {
    return propertiesLocal.some((property) => property.Name === prop);
  };
  const getPropertyByName = (propName) => {
    return propertiesLocal.find((property) => property.Name === propName);
  };

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      key={ItemID}
      variants={fadeIn(0.2)}
      className={itemCardClassName}
      onClick={() => bodyClickLocal && onBodyClick()}
      onContextMenu={handleContextMenu}
      onMouseDown={startHoldTimer}
      onMouseUp={cancelHoldTimer}
      onMouseLeave={cancelHoldTimer}
      onTouchStart={startHoldTimer}
      onTouchEnd={cancelHoldTimer}
      onTouchCancel={cancelHoldTimer}
      {...(bodyClickLocal ? hoverEffect(1, 1) : {})}
    >
      <div
        className={className + " CardDescContainer"}
        style={{
          gridTemplateColumns: gridColumns,
        }}
      >
        {createColumns()}
        {btnVisibleLoc ? (
          <div className="card-column button-column">
            <Button
              onClick={onEditBtnClick}
              btnStyle="btn-primary"
              btnMargin={"btn-small"}
              btnSize="btn-small"
              btnBehaviour="btn-fill"
              bNoShadow={true}
              icon={"edit"}
            >
              {btnText}
            </Button>
          </div>
        ) : bCountable && !isNarrow ? (
          <div className="card-column input">
            <Button
              btnSize="btn-small"
              btnStyle="btn-negative"
              btnBehaviour="btn-square"
              onClick={() => changeAmount(Amounts.get(ItemID) - 1)}
              btnMargin={"btn-small"}
              bNoShadow={true}
              className="ml-1"
            >
              -
            </Button>
            <InputField
              value={Amounts.get(ItemID)}
              placeholder={0}
              onChange={(e) => changeAmount(parseInt(e))}
              bNumeric={true}
              className="mb-0 ml-1 mr-1"
              copyEnabled={false}
            ></InputField>
            <Button
              btnSize="btn-small"
              btnStyle="btn-positive"
              btnBehaviour="btn-square"
              onClick={() => changeAmount(Amounts.get(ItemID) + 1)}
              btnMargin={"btn-small"}
              bNoShadow={true}
              className="mr-1"
            >
              +
            </Button>
          </div>
        ) : null}
      </div>
      <div className="narrow-mode-btn-container">
        {bCountable && isNarrow ? (
          <div className="card-column input">
            <Button
              btnSize="btn-small"
              btnStyle="btn-negative"
              btnBehaviour="btn-square"
              onClick={() => changeAmount(Amounts.get(ItemID) - 1)}
              btnMargin={"btn-small"}
              bNoShadow={true}
            >
              -
            </Button>
            <InputField
              value={Amounts.get(ItemID)}
              placeholder={0}
              onChange={(e) => changeAmount(parseInt(e))}
              inputMargins={["0px", "5px", "0px", "5px"]}
              bNumeric={true}
              className="mb-0"
              copyEnabled={false}
            ></InputField>
            <Button
              btnSize="btn-small"
              btnStyle="btn-positive"
              btnBehaviour="btn-square"
              onClick={() => changeAmount(Amounts.get(ItemID) + 1)}
              btnMargin={"btn-small"}
              bNoShadow={true}
            >
              +
            </Button>
          </div>
        ) : null}
      </div>
    </motion.div>
  );
}
